import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { History, SkipForward, PenTool, AlertCircle } from 'lucide-react';
import { Poll } from '../types';
import { apiService } from '../services/api';
import { authService } from '../services/auth';
import { PollCard } from '../components/PollCard';
import { CreatePollForm } from '../components/CreatePollForm';
import { useSignInModal } from '../context/SignInModalContext';
import './ActivityPage.css';

interface ActivityPageProps {
  showCreateForm?: boolean;
  onShowCreateFormChange?: (show: boolean) => void;
}

type TabType = 'voted' | 'skipped' | 'created';

export const ActivityPage: React.FC<ActivityPageProps> = ({
  showCreateForm = false,
  onShowCreateFormChange,
}) => {
  const userInfo = authService.getUserInfo();
  const userId = userInfo?.sub;
  const { showSignInModal } = useSignInModal();

  if (!userId) {
    return (
      <div className="activity-page">
        <div className="activity-container">
          <div className="activity-guest-state">
            <div className="guest-glow-orbs">
              <div className="orb orb-1"></div>
              <div className="orb orb-2"></div>
            </div>
            <div className="activity-guest-card">
              <div className="guest-card-header">
                <div className="icon-stack">
                  <div className="icon-wrapper icon-history">
                    <History size={24} />
                  </div>
                  <div className="icon-wrapper icon-skip">
                    <SkipForward size={24} />
                  </div>
                  <div className="icon-wrapper icon-created">
                    <PenTool size={24} />
                  </div>
                </div>
                <h2>Track Your Opoko Journey</h2>
              </div>
              <p className="guest-card-description">
                Sign in to view your complete dashboard. Track the polls you've voted in, organize the ones you've skipped, and manage your created questions all in one beautifully unified space.
              </p>
              <button className="guest-signin-btn" onClick={() => showSignInModal()}>
                Sign In to Opoko
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const activeTab: TabType = (urlTab === 'voted' || urlTab === 'skipped' || urlTab === 'created')
    ? urlTab
    : 'voted';

  const setActiveTab = (tab: TabType) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  };

  const [votedPolls, setVotedPolls] = useState<Poll[]>([]);
  const [skippedPolls, setSkippedPolls] = useState<Poll[]>([]);
  const [createdPolls, setCreatedPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActivity = async (showLoading = true) => {
    if (!userId) return;
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const data = await apiService.getUserActivity(userId);
      setVotedPolls(data.voted || []);
      setSkippedPolls(data.skipped || []);
      setCreatedPolls(data.created || []);
    } catch (err: any) {
      console.error('Failed to fetch activity:', err);
      setError(err.message || 'Failed to fetch user activity');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, [userId]);

  const handlePollCreated = (createdPoll: Poll) => {
    setCreatedPolls((prev) => [createdPoll, ...prev]);
    if (onShowCreateFormChange) {
      onShowCreateFormChange(false);
    }
  };

  const handleVoteUpdate = (updatedPoll: Poll) => {
    // If user votes or changes vote, update across all tabs
    const updateList = (list: Poll[]) =>
      list.map((p) => (p.pollId === updatedPoll.pollId ? updatedPoll : p));

    setVotedPolls(updateList);
    setCreatedPolls(updateList);
    setSkippedPolls(updateList);
  };

  const handlePollDelete = (pollId: string) => {
    const filterList = (list: Poll[]) => list.filter((p) => p.pollId !== pollId);

    setVotedPolls(filterList);
    setCreatedPolls(filterList);
    setSkippedPolls(filterList);
  };

  const getActiveList = () => {
    switch (activeTab) {
      case 'voted':
        return votedPolls;
      case 'skipped':
        return skippedPolls;
      case 'created':
        return createdPolls;
      default:
        return [];
    }
  };

  const activePolls = getActiveList();

  const getEmptyStateContent = () => {
    switch (activeTab) {
      case 'voted':
        return {
          icon: <History className="empty-icon" size={48} />,
          title: "No voted polls yet",
          description: "Explore the feed and vote on polls to see them here! Your opinion shapes the community.",
          actionText: "Browse Feed",
          actionPath: "/",
        };
      case 'skipped':
        return {
          icon: <SkipForward className="empty-icon" size={48} />,
          title: "No skipped polls",
          description: "You haven't skipped any polls yet. Keep making choices or skip polls you aren't interested in.",
          actionText: "Browse Feed",
          actionPath: "/",
        };
      case 'created':
        return {
          icon: <PenTool className="empty-icon" size={48} />,
          title: "No created polls",
          description: "Got an interesting question? Ask the community by creating your very first poll!",
          actionText: "Create Poll",
          actionFn: () => onShowCreateFormChange && onShowCreateFormChange(true),
        };
    }
  };

  const emptyState = getEmptyStateContent();

  return (
    <div className="activity-page">
      <div className="activity-container">


        {error && (
          <div className="activity-error-container">
            <AlertCircle className="error-icon" size={24} />
            <div className="error-details">
              <h4>Error loading activity</h4>
              <p>{error}</p>
              <button className="retry-btn" onClick={() => fetchActivity(true)}>
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        {!error && (
          <div className="activity-tabs">
            <button
              className={`activity-tab-btn ${activeTab === 'voted' ? 'active' : ''}`}
              onClick={() => setActiveTab('voted')}
            >
              <History size={16} />
              <span>Voted</span>
            </button>
            <button
              className={`activity-tab-btn ${activeTab === 'skipped' ? 'active' : ''}`}
              onClick={() => setActiveTab('skipped')}
            >
              <SkipForward size={16} />
              <span>Skipped</span>
            </button>
            <button
              className={`activity-tab-btn ${activeTab === 'created' ? 'active' : ''}`}
              onClick={() => setActiveTab('created')}
            >
              <PenTool size={16} />
              <span>Created</span>
            </button>
          </div>
        )}

        {/* Content Body */}
        {!error && (
          <div className="activity-content">
            {loading ? (
              <div className="shimmer-container">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="shimmer-card">
                    <div className="shimmer-header">
                      <div className="shimmer-avatar circle" />
                      <div className="shimmer-meta">
                        <div className="shimmer-line short" />
                        <div className="shimmer-line xshort" />
                      </div>
                    </div>
                    <div className="shimmer-title" />
                    <div className="shimmer-options">
                      <div className="shimmer-option" />
                      <div className="shimmer-option" />
                    </div>
                  </div>
                ))}
              </div>
            ) : activePolls.length > 0 ? (
              <div className="polls-list">
                {activePolls.map((poll) => (
                  <PollCard
                    key={poll.pollId}
                    poll={poll}
                    onVote={handleVoteUpdate}
                    onDelete={handlePollDelete}
                  />
                ))}
              </div>
            ) : (
              <div className="activity-empty-state">
                {emptyState.icon}
                <h3>{emptyState.title}</h3>
                <p>{emptyState.description}</p>
                {emptyState.actionText && (
                  emptyState.actionFn ? (
                    <button className="empty-action-btn" onClick={emptyState.actionFn}>
                      {emptyState.actionText}
                    </button>
                  ) : (
                    <a href={emptyState.actionPath} className="empty-action-btn-link">
                      {emptyState.actionText}
                    </a>
                  )
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {onShowCreateFormChange && (
        <CreatePollForm
          open={showCreateForm}
          onOpenChange={onShowCreateFormChange}
          onPollCreated={handlePollCreated}
        />
      )}
    </div>
  );
};
